/* My sessions — the participant's view of their own bookings. */

/*
  *** A LIST, NOT A GRID ***
  The library lobby is a grid because rooms are interchangeable and chosen
  by scanning. Sessions are chronological: the next one matters most, and a
  grid would put it beside rather than above the one after it.
*/
.list {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.cardMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.cardHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.when {
  margin: 0;
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text);
}

/* The mentor's local time, shown only when it differs from the viewer's. */
.altTime {
  margin: var(--space-1) 0 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  *** auto-fit, SO TWO FIELDS DO NOT LEAVE A GAP ***
  Three columns on a wide card, stacking on a narrow one, without a media
  query that would have to be kept in step with the card's own width.
*/
.meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: var(--space-3);
  margin: 0;
}

.meta dt {
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.meta dd {
  margin: var(--space-1) 0 0;
  font-size: var(--text-sm);
  color: var(--text);
}

.cancelled {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding-top: var(--space-3);
  border-top: 1px solid var(--border);
}

/*
  Why the Join button is disabled. It sits BESIDE the control rather than
  under the card, so the explanation is attached to the thing it explains.
*/
.hint {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/* Upcoming / Past. Same shape as the login card's tabs. */
.tabs {
  display: flex;
  gap: var(--space-1);
  padding: var(--space-1);
  background: var(--bg-sunken, var(--bg));
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  width: fit-content;
}

.tab {
  padding: var(--space-2) var(--space-4);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-muted);
  background: none;
  border: 0;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.tab:hover {
  color: var(--text);
}

.tabActive {
  color: var(--text);
  background: var(--bg-elevated);
  box-shadow: var(--shadow-sm, 0 1px 2px rgb(0 0 0 / 0.08));
}

/*
  How many sessions are running. Rendered only when non-zero — a "0" on
  every tab is noise, and the whole point of this badge is that it is
  almost never there.
*/
.tabCount {
  display: inline-grid;
  place-items: center;
  min-width: 1.25rem;
  height: 1.25rem;
  margin-left: var(--space-2);
  padding: 0 0.3rem;
  font-size: var(--text-xs);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #fff;
  background: var(--success);
  border-radius: var(--radius-full);
}

/* ------------------------------------------------------------- live */

/*
  *** A LIVE ROW LOOKS DIFFERENT, NOT JUST SORTED DIFFERENTLY ***
  The Live tab is opened in a hurry, usually to find one card among none.
  A tinted edge makes it the thing the eye lands on without reading.
*/
.cardLive {
  background: var(--success-soft);
  border-color: var(--success);
}

.livePill {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--success);
  background: var(--bg-elevated);
  border: 1px solid var(--success);
  border-radius: var(--radius-full);
}

.liveDot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-full);
  background: var(--success);
  animation: pulse 2s ease-in-out infinite;
}

/*
  Respected, not assumed: a pulsing dot is a problem for some people, and
  the pill says "12 min remaining" in words either way.
*/
@media (prefers-reduced-motion: reduce) {
  .liveDot {
    animation: none;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

.previewBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.muted {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  color: var(--danger);
  font-size: var(--text-sm);
}

/*
  Below this the three meta columns and the action row both want the full
  width, and the button row wrapping mid-label reads as broken.
*/
@media (max-width: 30rem) {
  .actions {
    align-items: stretch;
    flex-direction: column;
  }
}

/* The button under an empty-state sentence. */
.emptyAction {
  margin-top: var(--space-4);
}

/* ------------------------------------------------------ rate a session */

/* Already rated. A statement, not a control. */
.rated {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: rgb(15 23 42 / 45%);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: min(28rem, 100%);
  max-height: 85dvh;
  overflow-y: auto;
  padding: var(--space-5);
  background: var(--bg-elevated);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.dialogTitle {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--text);
}

.dialogSub {
  margin: calc(var(--space-4) * -1) 0 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.stars {
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.star {
  padding: 0 var(--space-1);
  font: inherit;
  /* Big enough to hit, and to read as the main control. */
  font-size: 1.75rem;
  line-height: 1;
  color: var(--border-strong);
  background: none;
  border: 0;
  cursor: pointer;
  transition: color var(--transition-fast);
}

.starOn {
  color: var(--amber);
}

/* The word beside the stars. Reserves its space so choosing one does not
   shift the dialog under the cursor. */
.starWord {
  min-width: 6rem;
  margin-left: var(--space-2);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.askRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.askLabel {
  font-size: var(--text-sm);
  color: var(--text);
}

.askButtons {
  display: flex;
  gap: var(--space-2);
}

.ask {
  padding: var(--space-1) var(--space-4);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  cursor: pointer;
}

.ask:hover {
  border-color: var(--border-strong);
}

/* Outlined rather than filled: these are answers, not the main action. */
.askOn {
  color: var(--indigo);
  border-color: var(--indigo);
  box-shadow: 0 0 0 1px var(--indigo);
}

.comment {
  padding: var(--space-3);
  font: inherit;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--text);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  resize: vertical;
}

.comment:focus {
  outline: 2px solid var(--indigo);
  outline-offset: -1px;
}

.dialogActions {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
}

@media (prefers-reduced-motion: reduce) {
  .star {
    transition: none;
  }
}
